import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getProjectBySlug } from '../data/projects';
import { getProjectContent } from '../data/projectContent';
import {
  ProjectLandingTemplate,
  ProjectPrivacyTemplate,
  ProjectSupportTemplate,
  ProjectTermsTemplate,
} from '../components/templates';

/**
 * ProjectPage Router
 *
 * Routes to the appropriate template based on project slug and page parameter.
 * URL patterns:
 *   /projects/:projectSlug         -> Landing page
 *   /projects/:projectSlug/privacy -> Privacy policy
 *   /projects/:projectSlug/support -> Support page
 *   /projects/:projectSlug/terms   -> Terms of service
 */
const ProjectPage: React.FC = () => {
  const { projectSlug, page } = useParams<{ projectSlug: string; page?: string }>();

  // Get project data
  const project = projectSlug ? getProjectBySlug(projectSlug) : undefined;

  // If project not found, redirect to projects list
  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  // Get project-specific content
  const content = getProjectContent(project.slug);

  // Route to appropriate template
  switch (page) {
    case 'privacy':
      if (content?.privacy) {
        return <ProjectPrivacyTemplate project={project} content={content.privacy} />;
      }
      // Fallback to landing if no privacy content
      return <Navigate to={`/projects/${project.slug}`} replace />;

    case 'support':
      if (content?.support) {
        return <ProjectSupportTemplate project={project} content={content.support} />;
      }
      // Fallback to landing if no support content
      return <Navigate to={`/projects/${project.slug}`} replace />;

    case 'terms':
      if (content?.terms) {
        return <ProjectTermsTemplate project={project} content={content.terms} />;
      }
      // Fallback to landing if no terms content
      return <Navigate to={`/projects/${project.slug}`} replace />;

    default:
      // Landing page
      return <ProjectLandingTemplate project={project} content={content?.landing} />;
  }
};

export default ProjectPage;
